<template>
    <nav class="navbar navbar-expand-lg navbar-light bg-light d-print-none">
        <div class="container">
            <a
                class="navbar-brand"
                href="/"
                :title="`${$t('company')} homepage`"
            >
                <img
                    :src="`/images/logos/logo-${($app.isArabic ? 'ar' : 'en')}.png`"
                    :alt="`${$t('company')} logo`"
                    loading="lazy"
                />
            </a>

            <button class="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarSupportedContent" aria-controls="navbarSupportedContent" aria-expanded="false" aria-label="Toggle navigation">
                <span class="navbar-toggler-icon"></span>
            </button>

            <div
                class="collapse navbar-collapse"
                id="navbarSupportedContent"
            >
                <ul class="navbar-nav ms-auto">
                    <li
                        v-for="item in items"
                        :key="item"
                        class="nav-item"
                        :class="{
                            active: ($route.name === item.name)
                        }"
                    >
                        <a
                            v-if="item.url"
                            class="nav-link"
                            :href="item.url"
                            :title="item.title"
                            target="_blank"
                        >
                            {{ item.title }}
                        </a>

                        <router-link
                            v-else
                            class="nav-link"
                            :to="item.path"
                            :title="item.title"
                        >
                            {{ item.title }}
                        </router-link>
                    </li>

                    <li
                        v-if="$app.auth"
                        class="nav-item dropdown"
                    >
                        <a class="nav-link dropdown-toggle" href="#" role="button" data-bs-toggle="dropdown" aria-expanded="false">
                            {{ $t("account") }}
                        </a>

                        <ul class="dropdown-menu">
                            <template v-if="$app.isArabic">
                                <li>
                                    <router-link
                                        class="dropdown-item"
                                        to="/arabic"
                                        :title="$t('allSeries')"
                                    >
                                        {{ $t("arabic.lessonsInArabic") }}
                                    </router-link>
                                </li>

                                <li>
                                    <hr class="dropdown-divider">
                                </li>
                            </template>

                            <template v-if="$app.canViewContent">
                                <li>
                                    <router-link
                                        class="dropdown-item"
                                        to="/home"
                                        :title="$t('allSeries')"
                                    >
                                        {{ $t("allSeries") }}
                                    </router-link>
                                </li>

                                <li>
                                    <hr class="dropdown-divider">
                                </li>
                            </template>

                            <li>
                                <a
                                    class="dropdown-item"
                                    href="/profile"
                                    :title="$t('profile')"
                                >
                                    {{ $t("profile") }}
                                </a>
                            </li>

                            <li>
                                <a
                                    class="dropdown-item"
                                    href="/billing"
                                    :title="$t('billing')"
                                >
                                    {{ $t("billing") }}
                                </a>
                            </li>

                            <li>
                                <a
                                    class="dropdown-item"
                                    href="/logout"
                                    :title="$t('auth.logout')"
                                >
                                    {{ $t("auth.logout") }}
                                </a>
                            </li>

                           <li v-if="$app.isImpersonating">
                                <a
                                    class="dropdown-item"
                                    :href="$app.impersonationLeaveUrl"
                                    :title="$t('auth.leaveImpersonation')"
                                >
                                    {{ $t("auth.leaveImpersonation") }}
                                </a>
                            </li>

                            <li>
                                <hr class="dropdown-divider">
                            </li>

                            <template v-if="$app.canAdministrateOrTranslate">
                                <li>
                                    <router-link
                                        v-if="$app.canAdministrate"
                                        class="dropdown-item"
                                        to="/admin"
                                        :title="$t('admin')"
                                    >
                                        {{ $t("admin") }}
                                    </router-link>

                                    <router-link
                                        v-else
                                        class="dropdown-item"
                                        to="/admin/series"
                                        :title="$t('helpTranslate')"
                                    >
                                        {{ $t("helpTranslate") }}
                                    </router-link>
                                </li>

                                <li>
                                    <hr class="dropdown-divider">
                                </li>
                            </template>

                            <li>
                                <router-link
                                    class="dropdown-item"
                                    to="/license-agreement"
                                    :title="$t('licenseAgreement')"
                                >
                                    {{ $t("licenseAgreement") }}
                                </router-link>
                            </li>

                            <li>
                                <router-link
                                    class="dropdown-item"
                                    to="/privacy-policy"
                                    :title="$t('privacyPolicy')"
                                >
                                    {{ $t("privacyPolicy") }}
                                </router-link>
                            </li>

                            <li>
                                <router-link
                                    class="dropdown-item"
                                    to="/cookie-policy"
                                    :title="$t('cookiePolicy')"
                                >
                                    {{ $t("cookiePolicy") }}
                                </router-link>
                            </li>
                        </ul>
                    </li>

                    <li
                        v-if="! $app.auth"
                        class="nav-item"
                    >
                        <a
                            class="nav-link"
                            href="/login"
                            :title="$t('auth.login')"
                        >
                            {{ $t("auth.login") }} <font-awesome-icon icon="fa-solid fa-arrow-right" />
                        </a>
                    </li>

                </ul>
            </div>
        </div>
    </nav>

    <div>
        <div class="container py-3">
          <ul class="list-inline text-center mb-0">
            <li
              v-for="row in $app.languages"
              class="list-inline-item mx-2"
            >
              <span class="badge text-bg-secondary text-bg-light">
                <a
                  :href="`${$app.appProtocol}://${row.subdomain}.${$app.appDomain}`"
                  class="text-decoration-none"
                >
                  {{ row.label }}
                </a>
              </span>
            </li>
          </ul>
        </div>
    </div>
</template>

<script>
export default {
    name: "Navigation",

    computed: {
        items() {
            return [
                {
                    path: "/",
                    name: "home",
                    title: this.$t('navigation.home'),
                },
                {
                    path: "/about",
                    name: "about",
                    title: this.$t('navigation.about'),
                },
                {
                    path: (this.$app.auth ? "/home" : "/material"),
                    name: "material",
                    title: this.$t('navigation.material'),
                },
                {
                    path: "/endorsements",
                    name: "endorsements",
                    title: this.$t('navigation.endorsements'),
                },
                {
                    url: "http://storage.tntministries.org.uk/newsletter/",
                    name: "news",
                    title: this.$t('navigation.news'),
                },
                {
                    path: "/contact",
                    name: "contact",
                    title: this.$t('navigation.contact'),
                },
            ];
        },
    },
};
</script>
